import {
  Allergen,
  SubscriptionItemType,
  SubscriptionItemVariantValue,
} from "@/primary-models";

export enum FreshRecipe {
  CLUCK = "CLUCK",
  GOBBLE = "GOBBLE",
  SHREDDED_CLUCK = "SHREDDED_CLUCK",
  SHREDDED_GOBBLE = "SHREDDED_GOBBLE",
  MOOO = "MOOO",
  OINK = "OINK",
  QUACK = "QUACK",
  SPLASH = "SPLASH",
  BAAA = "BAAA",
  SWISH = "SWISH",
}

export type TFreshRecipe = keyof typeof FreshRecipe;

// This is mostly to remind us to update the fresh recipe enum when we add a new recipe.
export const IS_FRESH_RECIPE: Record<TFreshRecipe, true>
& Record<Exclude<SubscriptionItemType, TFreshRecipe>, false> = {
  [SubscriptionItemType.CLUCK]: true,
  [SubscriptionItemType.GOBBLE]: true,
  [SubscriptionItemType.SHREDDED_CLUCK]: true,
  [SubscriptionItemType.SHREDDED_GOBBLE]: true,
  [SubscriptionItemType.MOOO]: true,
  [SubscriptionItemType.OINK]: true,
  [SubscriptionItemType.QUACK]: true,
  [SubscriptionItemType.SPLASH]: true,
  [SubscriptionItemType.BAAA]: true,
  [SubscriptionItemType.SWISH]: true,
  [SubscriptionItemType.SCOOP_PLANET]: false,
  [SubscriptionItemType.SCOOP_HEALTH]: false,
  [SubscriptionItemType.NIBBLES_SALMON]: false,
  [SubscriptionItemType.NIBBLES_CHICKEN]: false,
  [SubscriptionItemType.NIBBLES_SALMON_POUCH]: false,
  [SubscriptionItemType.NIBBLES_CHICKEN_POUCH]: false,
  [SubscriptionItemType.NIBBLES_TUNA_POUCH]: false,
  [SubscriptionItemType.SPRINKLES]: false,
  [SubscriptionItemType.TOTE_BAG]: false,
};

export function isFreshRecipe(type: string): type is TFreshRecipe {
  return !!(IS_FRESH_RECIPE as Partial<Record<string, boolean>>)[type];
}

export type TFreshRecipeQuantities = Record<TFreshRecipe, number | null | undefined>;

export interface IUpdateRecipesRequest {
  subscriptionId: number;
  recipes: Partial<TFreshRecipeQuantities>;
}

export interface IUpdateRecipesResponse {
  subscriptionId: number;
  recipes:TFreshRecipeQuantities;
}

export const RECIPES_TO_ALLERGEN_MAP: Record<TFreshRecipe, Allergen[]> = {
  [FreshRecipe.CLUCK]: [
    Allergen.CHICKEN,
  ],
  [FreshRecipe.GOBBLE]: [
    Allergen.CHICKEN,
    Allergen.TURKEY,
  ],
  [FreshRecipe.SHREDDED_CLUCK]: [
    Allergen.CHICKEN,
    Allergen.SALMON,
  ],
  [FreshRecipe.SHREDDED_GOBBLE]: [
    Allergen.CHICKEN,
    Allergen.TURKEY,
    Allergen.SALMON,
  ],
  [FreshRecipe.MOOO]: [
    Allergen.BEEF,
    Allergen.SALMON,
  ],
  [FreshRecipe.OINK]: [
    Allergen.PORK,
    Allergen.SALMON,
  ],
  [FreshRecipe.QUACK]: [
    Allergen.CHICKEN,
    Allergen.DUCK,
    Allergen.SALMON,
  ],
  [FreshRecipe.SPLASH]: [
    Allergen.TURKEY,
    Allergen.WHITEFISH,
    Allergen.SALMON,
  ],
  [FreshRecipe.BAAA]: [
    Allergen.BEEF,
    Allergen.LAMB,
    Allergen.SALMON,
  ],
  [FreshRecipe.SWISH]: [
    Allergen.TUNA,
    Allergen.SALMON,
    Allergen.CHICKEN,
  ],
};

export const ALLERGEN_TO_RECIPES_MAP: Record<Allergen, TFreshRecipe[]> =
  Object.entries(RECIPES_TO_ALLERGEN_MAP).reduce((((acc, [ recipe, allergens ]) => {
    allergens.forEach((allergen) => {
      acc[allergen].push(recipe as FreshRecipe);
    });
    return acc;
  })), ({
    CHICKEN: [],
    TURKEY: [],
    BEEF: [],
    PORK: [],
    LAMB: [],
    WHITEFISH: [],
    DUCK: [],
    SALMON: [],
    TUNA: [],
  } satisfies typeof ALLERGEN_TO_RECIPES_MAP as typeof ALLERGEN_TO_RECIPES_MAP));

export const containsAllergen = (
  recipe: TFreshRecipe,
  allergen: Allergen,
) => ALLERGEN_TO_RECIPES_MAP[allergen].includes(recipe);

export const FRESH_RECIPE_VARIANTS: ReadonlyArray<SubscriptionItemVariantValue> = [
  SubscriptionItemVariantValue.A,
  SubscriptionItemVariantValue.B,
  SubscriptionItemVariantValue.C,
  SubscriptionItemVariantValue.D,
  SubscriptionItemVariantValue.E,
  SubscriptionItemVariantValue.F,
];

export interface IGetFreshMealPlanPricingInfoMapConfig {
  includeTrialPlans?: boolean,
}
export const SHREDDED_FRESH_RECIPES: ReadonlyArray<TFreshRecipe> = [
  FreshRecipe.SHREDDED_CLUCK,
  FreshRecipe.SHREDDED_GOBBLE,
];

export const isShreddedFreshRecipe = (name: SubscriptionItemType): boolean =>
  (SHREDDED_FRESH_RECIPES as ReadonlyArray<SubscriptionItemType>).includes(name);

export const hasActiveShreddedItem = (
  freshMealPlans: ReadonlyArray<{ items: ReadonlyArray<{ name: SubscriptionItemType }> }>,
): boolean =>
  freshMealPlans.some((plan) => plan.items.some((item) => isShreddedFreshRecipe(item.name)));
